'use client';
import { useContext, useState } from 'react';
import { clsx } from 'clsx';
import { Icon } from 'components/UI/Icon';
import Link from 'components/UI/Link/Link';
import { Title, type TitleLink } from 'components/UI/Title';
import { AppContext } from 'context/AppContext';
import type { MenuItemProps } from 'types/menu';
import type { PropsWithClassName } from 'types/react-props';
import styles from './seobox.module.scss';

type SeoboxItemProps = {
  children: Array<MenuItemProps>;
  type: string;
  title: string;
  noFollow: boolean;
  childIndex: number;
  valid: boolean;
  active: boolean;
};

export interface WidgetMenuSeoboxProps {
  readonly model: {
    readonly menu: Array<SeoboxItemProps>;
  };
  readonly title?: string;
  readonly link?: TitleLink;
  readonly view?: string;
  readonly id?: string;
  readonly template?: string;
  readonly type?: string;
}

type LinkGroupProps = PropsWithClassName<{
  readonly group: SeoboxItemProps;
}>;

const LinkGroup = ({ group, className }: LinkGroupProps) => {
  const {
    pageMetaData: { isStoryline },
  } = useContext(AppContext);
  const [open, setOpen] = useState(false);

  const DynamicTag = isStoryline ? 'div' : 'h3';

  return (
    <div className={styles.groupWrapper}>
      <div className={clsx(styles.group, { [styles.open]: open }, className)} key={group.title}>
        <DynamicTag className={styles.headline}>{group.title}</DynamicTag>
        {group.children?.map((link) => (
          <Link
            key={link.title ?? link.url}
            className={styles.link}
            href={link.url ?? ''}
            target={link.target ?? '_self'}
          >
            {link.title ?? link.url}
          </Link>
        ))}
      </div>
      <button
        className={clsx(
          styles.showMore,
          styles.mobile,
          open && styles.open,
          group.children && group.children?.length <= 5 && styles.hide
        )}
        type="button"
        onClick={() => setOpen(!open)}
      >
        {open ? 'weniger' : 'mehr'} anzeigen
        <Icon name="caretSmallDown" />
      </button>
    </div>
  );
};

export const WidgetMenuSeobox = ({ context }: { context: WidgetMenuSeoboxProps }) => {
  const [open, setOpen] = useState(false);
  const {
    pageMetaData: { noAds, isStoryline },
  } = useContext(AppContext);

  if (noAds) {
    return null;
  }

  return (
    <section>
      {context.title && <Title as={isStoryline ? 'div' : 'h2'}>{context.title}</Title>}
      <div className={styles.wrapper}>
        {context.model?.menu?.map((group) => (
          <LinkGroup group={group} className={clsx(open && styles.open)} key={group.title} />
        ))}
      </div>
      <button className={clsx(styles.showMore, styles.desktop, open && styles.open)} onClick={() => setOpen(!open)}>
        {open ? 'weniger' : 'mehr'} anzeigen
        <Icon name="caretSmallDown" />
      </button>
    </section>
  );
};
